/**
 * Classification domain constants: sets, predicates and business rules.
 *
 * Display strings live in labels.ts. Both files import from types.ts only,
 * never from each other — this keeps the dependency graph one-way and
 * prevents the circular-init bug that bit us when a label re-export here
 * triggered labels.ts before DESTINATARIO_LABELS was initialized.
 */

import { ClassificationDocumentType } from "./types";

/**
 * Per-incentive fiscal_category vocabulary. Drives the review dropdown so
 * the reviewer of a música row doesn't see cine categories and vice-versa.
 * MUST stay in sync with the rulesets declared in
 * services/classifier/app/rulesets/invoice/*.json and with
 * FISCAL_CATEGORIES_BY_INCENTIVE in
 * services/api/app/domain/classifications/category_mapping.py.
 */
export const TaxIncentiveType = {
  FILM_AUDIOVISUAL: "film_audiovisual",
  PERFORMING_ARTS_MUSIC: "performing_arts_music",
} as const;

export type TaxIncentiveTypeValue = (typeof TaxIncentiveType)[keyof typeof TaxIncentiveType];

export const FISCAL_CATEGORIES_BY_INCENTIVE: Record<TaxIncentiveTypeValue, readonly string[]> = {
  [TaxIncentiveType.PERFORMING_ARTS_MUSIC]: [
    "gastos_artisticos",
    "gastos_tecnicos",
    "gastos_promocionales",
    "gastos_no_elegibles",
  ],
  [TaxIncentiveType.FILM_AUDIOVISUAL]: [
    "gastos_produccion",
    "gastos_promocionales",
    "gastos_no_elegibles",
  ],
};

/**
 * Flattened set of every assignable fiscal_category key. Used as a
 * last-resort fallback when no incentive_type is provided to the dropdown
 * (shouldn't happen for new beneficiaries, but legacy rows might).
 * Critically excludes the BUCKET_* keys ("artistic", "technical", …) and
 * the legacy F0/F1/F2 keys — the backend rejects both with a 422.
 */
export const ALL_FISCAL_CATEGORY_KEYS: readonly string[] = Object.freeze(
  Array.from(new Set(Object.values(FISCAL_CATEGORIES_BY_INCENTIVE).flat())),
);

/**
 * fiscal_category meaning "non-eligible expense". A DEDUCIBLE/CONDICIONAL row
 * can never carry it — the backend rejects that pair with a 422 (#253). Mirror
 * of NON_ELIGIBLE_FISCAL_CATEGORY in
 * services/api/app/domain/classifications/eligibility_coherence.py.
 */
export const NON_ELIGIBLE_FISCAL_CATEGORY = "gastos_no_elegibles";

/**
 * Canonical display order for the fiscal **buckets** in the classification
 * summary (artistic / technical / promotional / production /
 * other_non_deductible). These mirror BUCKET_* in
 * services/api/app/domain/classifications/category_mapping.py and are
 * distinct from the assignable category keys above (gastos_artisticos…)
 * — the summary aggregates rows into buckets, never raw categories.
 *
 * Order is fixed by accounting convention so the overview reads the same
 * way for every beneficiary of the same incentive type. Buckets that
 * don't appear here (defensive — backend could add new ones) are
 * appended in arrival order.
 */
export const FISCAL_BUCKETS_BY_INCENTIVE: Record<TaxIncentiveTypeValue, readonly string[]> = {
  [TaxIncentiveType.PERFORMING_ARTS_MUSIC]: [
    "artistic",
    "technical",
    "promotional",
    "other_non_deductible",
  ],
  [TaxIncentiveType.FILM_AUDIOVISUAL]: ["production", "promotional", "other_non_deductible"],
};

/**
 * Resolve the assignable fiscal_category options for a beneficiary based on
 * its incentive_type. Falls back to ALL_FISCAL_CATEGORY_KEYS when the
 * incentive_type is null/unknown (legacy rows) so the reviewer is never
 * stuck without options.
 */
export function fiscalCategoryOptionsForIncentive(
  incentiveType: TaxIncentiveTypeValue | string | null | undefined,
): readonly string[] {
  if (incentiveType && incentiveType in FISCAL_CATEGORIES_BY_INCENTIVE) {
    return FISCAL_CATEGORIES_BY_INCENTIVE[incentiveType as TaxIncentiveTypeValue];
  }
  return ALL_FISCAL_CATEGORY_KEYS;
}

// ── Expense document types (mirrors backend EXPENSE_DOCUMENT_TYPES) ─

export const EXPENSE_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  ClassificationDocumentType.FACTURA,
  ClassificationDocumentType.NOMINA,
]);

export function isExpenseDocumentType(type: string | null | undefined): boolean {
  return !!type && EXPENSE_DOCUMENT_TYPES.has(type);
}
